<script >
import ChartingLiteChart from '@/features/charting-next/components/ChartingLiteChart.vue';
import ChartingWorkspaceMetricStrip from '@/features/charting-next/components/workspace/ChartingWorkspaceMetricStrip.vue';
import { computed, ref } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  stock: {
    type: Object,
    default: null
  },
  chartSeries: {
    type: Object,
    default: () => ({
      candles: [],
      volumes: [],
      corporateActions: []
    })
  },
  isChartLoading: {
    type: Boolean,
    default: false
  },
  chartError: {
    type: [Object, String],
    default: ''
  },
  resolution: {
    type: String,
    default: 'M'
  },
  priceSeriesType: {
    type: String,
    default: 'bars'
  },
  showVolume: {
    type: Boolean,
    default: false
  },
  overlayVisibility: {
    type: Object,
    default: () => ({
      support: true,
      stop: true,
      resistance: true,
      trendLine: true,
      trendPoint: true,
      riskRewardPoint: true
    })
  },
  metrics: {
    type: Array,
    default: () => []
  },
  availableMetrics: {
    type: Array,
    default: () => []
  },
  isMetricsLoading: {
    type: Boolean,
    default: false
  },
  isMetricMutating: {
    type: Boolean,
    default: false
  },
  metricError: {
    type: [Object, String],
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const overlayControls = computed(() => {
    const priceLines = props.stock?.chartOverlays?.priceLines || {};
    return [{
      key: 'support',
      label: 'Soporte',
      available: Boolean(priceLines.support),
      active: Boolean(props.overlayVisibility.support && priceLines.support),
      title: priceLines.support ? 'Activa o desactiva el soporte.' : 'No existe soporte.'
    }, {
      key: 'stop',
      label: 'Stop',
      available: Boolean(priceLines.stop),
      active: Boolean(props.overlayVisibility.stop && priceLines.stop),
      title: priceLines.stop ? 'Activa o desactiva el stop.' : 'No existe stop.'
    }, {
      key: 'resistance',
      label: 'Resistencia',
      available: Boolean(priceLines.resistance),
      active: Boolean(props.overlayVisibility.resistance && priceLines.resistance),
      title: priceLines.resistance ? 'Activa o desactiva la resistencia.' : 'No existe resistencia.'
    }, {
      key: 'trendLine',
      label: 'LT',
      available: Boolean(props.stock?.chartOverlays?.trendLine),
      active: Boolean(props.overlayVisibility.trendLine && props.stock?.chartOverlays?.trendLine && props.resolution === 'M' && props.priceSeriesType !== 'area'),
      title: !props.stock?.chartOverlays?.trendLine ? 'No existe linea LT.' : props.priceSeriesType === 'area' ? 'La linea LT no se muestra en modo area.' : props.resolution === 'M' ? 'Activa o desactiva la linea LT.' : 'La linea LT solo se muestra en mensual.'
    }, {
      key: 'riskRewardPoint',
      label: 'Punto RR',
      available: Boolean(props.stock?.chartOverlays?.riskRewardPoint),
      active: Boolean(props.overlayVisibility.riskRewardPoint && props.stock?.chartOverlays?.riskRewardPoint),
      title: props.stock?.chartOverlays?.riskRewardPoint ? 'Activa o desactiva el punto RR.' : 'No existe punto RR.'
    }];
  });
  const emit = __ctx.emit;
  const legendExpanded = ref(true);
  const settingsPopover = ref(null);
  const toggleSettingsPopover = event => {
    settingsPopover.value?.toggle(event);
  };
  return {
    props,
    overlayControls,
    emit,
    legendExpanded,
    settingsPopover,
    toggleSettingsPopover
  };
};
__sfc_main.components = Object.assign({
  ChartingWorkspaceMetricStrip,
  ChartingLiteChart
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <section class="charting-b-stage">
        <div class="charting-b-stage-inner">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-center">
                <ChartingWorkspaceMetricStrip
                    :metrics="props.metrics"
                    :available-metrics="props.availableMetrics"
                    :is-loading="props.isMetricsLoading"
                    :is-mutating="props.isMetricMutating"
                    :error="props.metricError"
                    @add-metric="emit('add-metric', $event)"
                    @remove-metric="emit('remove-metric', $event)"
                />
            </div>

            <div class="charting-b-stage-surface">
                <div class="charting-b-stage-chart-shell">
                    <div class="charting-b-stage-rail" aria-label="Controles del gráfico">
                        <Button label="D" size="small" class="charting-b-stage-rail-button" :severity="props.resolution === 'D' ? 'primary' : 'secondary'" :outlined="props.resolution !== 'D'" @click="emit('set-resolution', 'D')" />
                        <Button label="W" size="small" class="charting-b-stage-rail-button" :severity="props.resolution === 'W' ? 'primary' : 'secondary'" :outlined="props.resolution !== 'W'" @click="emit('set-resolution', 'W')" />
                        <Button label="M" size="small" class="charting-b-stage-rail-button" :severity="props.resolution === 'M' ? 'primary' : 'secondary'" :outlined="props.resolution !== 'M'" @click="emit('set-resolution', 'M')" />
                        <Button icon="pi pi-cog" size="small" class="charting-b-stage-rail-button" severity="secondary" outlined aria-label="Ajustes del gráfico" @click="toggleSettingsPopover" />
                    </div>

                    <div class="charting-b-stage-chart-canvas">
                        <div v-if="props.isChartLoading" class="charting-b-stage-status">
                            <ProgressSpinner style="width: 32px; height: 32px" stroke-width="6" />
                        </div>
                        <div v-else-if="props.chartError" class="charting-b-stage-status">
                            <div class="brand-banner-warning rounded-xl border p-4 text-sm">No se pudo cargar la serie OHLC del ticker seleccionado.</div>
                        </div>
                        <div v-else-if="!props.chartSeries || !props.chartSeries.candles || !props.chartSeries.candles.length" class="charting-b-stage-status">
                            <div class="brand-empty-state rounded-xl border border-dashed p-4 text-sm">No hay velas disponibles para este ticker y periodo.</div>
                        </div>
                        <ChartingLiteChart
                            v-else
                            legend-placement="external"
                            :legend-expanded="legendExpanded"
                            @update:legend-expanded="legendExpanded = $event"
                            :candles="props.chartSeries.candles"
                            :has-full-history="Boolean(props.chartSeries.hasFullHistory)"
                            :volumes="props.chartSeries.volumes"
                            :corporate-actions="props.chartSeries.corporateActions"
                            :analysis-overlays="(props.stock && props.stock.chartOverlays) || {}"
                            :overlay-visibility="props.overlayVisibility"
                            :resolution="props.resolution"
                            :price-series-type="props.priceSeriesType"
                            :show-volume="props.showVolume"
                            :ticker="(props.stock && props.stock.ticker) || ''"
                            :company-name="(props.stock && (props.stock.companyName || props.stock.description)) || ''"
                            :entry-type-label="(props.stock && props.stock.entryTypeLabel) || ''"
                            :series-key="`${(props.stock && props.stock.ticker) || 'none'}:${props.resolution}:${props.priceSeriesType}`"
                        >
                            <template #header-actions><slot name="stock-actions" /></template>
                        </ChartingLiteChart>
                    </div>
                </div>
            </div>
        </div>

        <Popover ref="settingsPopover">
            <div class="charting-b-stage-settings-popover">
                <div v-if="props.stock && props.stock.hasMultipleEntries" class="charting-b-stage-settings-section">
                    <div class="charting-b-stage-settings-title">Entradas</div>
                    <div class="charting-b-stage-settings-grid">
                        <Button
                            v-for="variant in props.stock.entryVariants"
                            :key="variant.entryVariantKey"
                            :label="variant.entrySwitchLabel"
                            size="small"
                            :severity="props.stock.activeEntryVariantKey === variant.entryVariantKey ? 'primary' : 'secondary'"
                            :outlined="props.stock.activeEntryVariantKey !== variant.entryVariantKey"
                            @click="emit('select-entry-variant', variant.entryVariantKey)"
                        />
                    </div>
                </div>

                <div class="charting-b-stage-settings-section">
                    <div class="charting-b-stage-settings-title">Vista</div>
                    <div class="charting-b-stage-settings-grid">
                        <Button
                            label="Area"
                            size="small"
                            :severity="props.priceSeriesType === 'area' ? 'primary' : 'secondary'"
                            :outlined="props.priceSeriesType !== 'area'"
                            @click="emit('set-price-series-type', props.priceSeriesType === 'area' ? 'bars' : 'area')"
                        />
                        <Button label="Vol" size="small" :severity="props.showVolume ? 'primary' : 'secondary'" :outlined="!props.showVolume" @click="emit('toggle-volume')" />
                    </div>
                </div>

                <div v-if="props.stock" class="charting-b-stage-settings-section">
                    <div class="charting-b-stage-settings-title">Capas</div>
                    <div class="charting-b-stage-settings-grid">
                        <Button
                            v-for="control in overlayControls"
                            :key="control.key"
                            :label="control.label"
                            size="small"
                            :severity="control.active ? 'primary' : 'secondary'"
                            :outlined="!control.active"
                            :disabled="!control.available"
                            :title="control.title"
                            @click="emit('toggle-overlay-visibility', control.key)"
                        />
                    </div>
                </div>
            </div>
        </Popover>
    </section>
</template>

<style scoped>
.charting-b-stage {
    min-width: 0;
    min-height: 0;
    height: 100%;
}

.charting-b-stage-inner {
    display: flex;
    min-height: 0;
    height: 100%;
    flex-direction: column;
    gap: 0.75rem;
}

.charting-b-stage-surface {
    min-height: 0;
    flex: 1;
    border: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    border-radius: 1.5rem;
    overflow: hidden;
    background: var(--charting-b-stage-bg, radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 28%), linear-gradient(180deg, rgba(248, 250, 252, 0.96), rgba(255, 255, 255, 1)));
    position: relative;
}

.charting-b-stage-chart-shell {
    display: flex;
    min-height: 0;
    height: 100%;
}

.charting-b-stage-rail {
    display: flex;
    width: 3.5rem;
    flex: 0 0 3.5rem;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    border-right: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    background: color-mix(in srgb, var(--charting-b-panel-bg-strong, #ffffff) 80%, transparent);
    padding: 0.65rem 0.5rem;
}

::v-deep .charting-b-stage-rail-button.p-button {
    width: 2.35rem;
    min-width: 2.35rem;
    height: 2.35rem;
    justify-content: center;
    padding: 0;
}

.charting-b-stage-chart-canvas {
    min-width: 0;
    min-height: 0;
    flex: 1 1 auto;
}

.charting-b-stage-settings-popover {
    display: flex;
    width: min(22rem, 82vw);
    flex-direction: column;
    gap: 0.95rem;
}

.charting-b-stage-settings-section {
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
}

.charting-b-stage-settings-title {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--p-text-muted-color);
}

.charting-b-stage-settings-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
}

.charting-b-stage-status {
    min-height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

@media (max-width: 1199px) {
    .charting-b-stage {
        height: auto;
    }

    .charting-b-stage-inner {
        height: auto;
    }

    .charting-b-stage-chart-shell {
        min-height: 30rem;
        flex-direction: column;
    }

    .charting-b-stage-rail {
        width: 100%;
        flex: 0 0 auto;
        flex-direction: row;
        justify-content: flex-start;
        border-right: 0;
        border-bottom: 1px solid var(--charting-b-border-color, rgba(148, 163, 184, 0.18));
    }

    .charting-b-stage-chart-canvas {
        min-height: 30rem;
    }

    .charting-b-stage-surface,
    .charting-b-stage-status {
        min-height: 30rem;
    }
}
</style>
